import type { Item } from '@ads/components-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { getApiV1NotificationMobileAppSharedCreateByUser } from '@/shared/api/generated/clients/notificationMobileAppController/getApiV1NotificationMobileAppSharedCreateByUser';

const DEBOUNCE_MS = 2000;
const MIN_CHARS = 3;
const PAGE_SIZE = 20;

/**
 * Busca remetentes de notificação com debounce de 2 s e mínimo de 3 caracteres.
 * Limpa os resultados imediatamente ao digitar; a busca real dispara após o debounce.
 * Páginas seguintes são buscadas em background enquanto `isOpen` for `true`.
 *
 * @param filter - Texto digitado pelo usuário
 * @param isOpen - Controla se o auto-fetch de páginas seguintes está ativo (ex: dropdown aberto)
 * @returns Lista acumulada de remetentes e estado de sucesso da query
 *
 * @example
 * ```tsx
 * const { items, isQuerySuccess } = useNotificationSenderSearch(sentBySearch, open);
 * ```
 */
export function useNotificationSenderSearch(filter: string, isOpen: boolean) {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filter]);

  const isPending = filter !== debouncedFilter;
  const isReady = !isPending && debouncedFilter.length >= MIN_CHARS;

  const query = useInfiniteQuery({
    queryKey: ['notification-sender-search', debouncedFilter],
    queryFn: ({ pageParam }) =>
      getApiV1NotificationMobileAppSharedCreateByUser({
        searchText: debouncedFilter,
        pageNumber: pageParam,
        pageSize: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === PAGE_SIZE ? allPages.length + 1 : undefined,
    enabled: isReady,
  });

  // Auto-fetch páginas seguintes enquanto o dropdown estiver aberto
  useEffect(() => {
    if (isOpen && query.hasNextPage && !query.isFetchingNextPage) {
      void query.fetchNextPage();
    }
  }, [isOpen, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const items: Item<string>[] = useMemo(
    () =>
      isPending
        ? []
        : (query.data?.pages.flat() ?? []).map(item => ({
            label: item.value ?? '',
            value: item.key?.toString() ?? '',
          })),
    [isPending, query.data],
  );

  return { items, isQuerySuccess: !isPending && query.isSuccess } as const;
}
